/**
 * Pick indicators + Review in-place editing (specs/challenge-flow-redesign AC-3.9, AC-3.10, AC-4.7).
 * Pure functions so the counting / matching rules are tested without rendering.
 */

import type { Game, MarketOdds, PickSlip } from '../types/create.types';

type IsSelected = (gameId: string, marketId: number, value: string) => boolean;

/**
 * Picks per date for the list view's date strip (AC-3.9). Keyed by `Game.date`, the same
 * `YYYY-MM-DD` key `createApi` groups fixtures by, so a late kickoff lands on the day it is listed
 * under. Counts one per slip, like the per-game dot. A slip whose game is not loaded has no known
 * date and is skipped.
 */
export function countPicksByDate(slips: readonly PickSlip[], games: readonly Game[]): Record<string, number> {
  const dateByGameId = new Map(games.map((g) => [g.id, g.date]));
  const counts: Record<string, number> = {};
  for (const slip of slips) {
    const date = dateByGameId.get(slip.gameId);
    if (!date) continue;
    counts[date] = (counts[date] ?? 0) + 1;
  }
  return counts;
}

/**
 * True when a (possibly merged) market chip holds a pick for the game (AC-3.10). Goes through the
 * same `isSelected` the odds tiles use, so a 1X2 slip (no `marketId`) and any line of a merged
 * O/U or handicap chip resolve exactly as they do when the tile is highlighted.
 */
export function marketHasPick(market: MarketOdds, gameId: string, isSelected: IsSelected): boolean {
  return market.values.some((v) => isSelected(gameId, v.marketId ?? market.id, v.value));
}

/**
 * The market a Review row edits (AC-4.7): the chip holding the slip's market. A match-winner slip
 * has no `marketId`, so it maps to the sport's primary market; a single O/U or handicap line maps to
 * its merged family chip. Null when the market is no longer offered.
 */
export function findSlipMarket(
  merged: readonly MarketOdds[],
  slip: PickSlip,
  primaryMarketId: number
): MarketOdds | null {
  const target = slip.marketId ?? primaryMarketId;
  return merged.find((m) => m.id === target || m.values.some((v) => v.marketId === target)) ?? null;
}

/**
 * Review edits swap a pick but never remove it (AC-4.7): the shared pick handler toggles a slip
 * off when its current value is tapped again, so that tap must not reach it.
 */
export function shouldApplyReviewEdit(
  isSelected: IsSelected,
  gameId: string,
  marketId: number,
  value: string
): boolean {
  return !isSelected(gameId, marketId, value);
}
